import { deleteComplimentEvent, referencedComplimentLabels } from '../lib/complimentEvents'
import { postgresTextArray } from '../lib/postgresTextArray'
import {readPendingWear,preparePendingWear,clearPendingWear,sendPendingWearWithWeather as sendPendingWear,isDefiniteWearRefusal} from '../lib/atomicWearCreate'
import { MOST_WORN_MINIMUM_SECONDS } from '../lib/primaryWearModel'
const createWearLocks=new Set()
const pendingWearEvent='fos-pending-wear-create-changed'
import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '../lib/supabase'
import { subscribeWearWeather } from '../lib/wearLogContext'
import { useAuth } from '../App'
import { emitWearLogged } from '../lib/pwaInstall'
import {
  applyWearLogChange,
  publishWearLogChange,
  reconcileWearLogSnapshot,
  subscribeToWearLogChanges,
} from '../lib/wearLogEvents'
import {
  createComplimentEvent,
  fetchCanonicalContextTags,
  hydrateComplimentData,
} from '../lib/complimentEvents'
import { prepareWearSession } from '../lib/wearSessions'
import { canonicalizeWearOccasion } from '../lib/wearOccasions'
import { useFeatureFlag } from './useFeatureFlag'

// Wear Model v2 (ratified 2026-07-17):
// - A wear-day may hold multiple wear_logs rows, distinguished by wear_label.
//   Exactly one 'sotd' per user-day (DB partial unique index enforces it).
// - Every physical application (initial or reapply) is a row in
//   fragrance.wear_applications with its own timestamp + spray count.
// - wear_logs.sprays is a TRIGGER-MAINTAINED TOTAL (sum of applications).
//   Never write wear_logs.sprays directly — edit the application rows.

function normalizeEntry(entry) {
  // Canonical columns only (occasion / note / worn_on) — the legacy trio
  // (context / notes / wear_date) is dropped per work_item 19. Legacy keys are
  // still ACCEPTED as input for old callers, but never emitted to the DB.
  const {
    wear_date: legacyWornOn,
    context: legacyOccasion,
    notes: legacyNote,
    compliments: _legacyCompliments,
    compliment_source: _legacyComplimentSource,
    ...rest
  } = entry
  return {
    ...rest,
    worn_on: rest.worn_on || legacyWornOn,
    occasion: canonicalizeWearOccasion(rest.occasion || legacyOccasion),
    note: rest.note || legacyNote || null,
  }
}

function normalizePatch(patch) {
  const out = { ...patch }
  const hasWornOn = 'worn_on' in patch || 'wear_date' in patch
  const hasOccasion = 'occasion' in patch || 'context' in patch
  const hasNote = 'note' in patch || 'notes' in patch
  if (hasWornOn) out.worn_on = patch.worn_on || patch.wear_date
  if (hasOccasion) out.occasion = canonicalizeWearOccasion(patch.occasion || patch.context)
  if (hasNote) out.note = patch.note || patch.notes || null
  // Never send the dropped legacy columns to the DB.
  delete out.wear_date
  delete out.context
  delete out.notes
  // sprays is trigger-maintained — strip it from direct log patches.
  delete out.sprays
  // Event rows own all new compliment writes. These columns remain read-only
  // solely for zero-event legacy fallback.
  delete out.compliments
  delete out.compliment_source
  return out
}

function isSotdConflict(error) {
  const message = `${error?.message || ''} ${error?.details || ''}`
  return /wear_logs_one_sotd_per_day|already have an SOTD/i.test(message)
}

function applicationsTotal(applications) {
  return (applications || []).reduce((sum, app) => sum + Number(app.sprays || 0), 0) || null
}

export function useWearLog({ limit = 50, fragranceId = null, enableWeatherSnapshot = false } = {}) {
  const { user } = useAuth()
  const primaryWearModelEnabled = useFeatureFlag('primary_wear_model')
  const [logs, setLogs] = useState([])
  const [durationMostWorn, setDurationMostWorn] = useState([])
  const [pendingCreateState,setPendingCreateState]=useState(null)
  const [pendingCreateError,setPendingCreateError]=useState('')
  const refreshPendingCreate=useCallback(()=>{try{setPendingCreateState(readPendingWear(localStorage,user?.id));setPendingCreateError('')}catch(error){setPendingCreateError(error.message)}},[user?.id])
  useEffect(()=>{refreshPendingCreate();window.addEventListener('storage',refreshPendingCreate);window.addEventListener(pendingWearEvent,refreshPendingCreate);return()=>{window.removeEventListener('storage',refreshPendingCreate);window.removeEventListener(pendingWearEvent,refreshPendingCreate)}},[refreshPendingCreate])
  const pendingCreate=pendingCreateState?.ownerId===user?.id?pendingCreateState:null

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [contextTags, setContextTags] = useState([])
  const [recentWhoLabels, setRecentWhoLabels] = useState([])
  const [recentWhereLabels, setRecentWhereLabels] = useState([])
  const fetchVersion = useRef(0)
  const pendingChanges = useRef(new Map())
  const logSources = useRef(new Map())
  const weatherPatches = useRef(new Map())

  const fetchLogs = useCallback(async ({ silent = false } = {}) => {
    const version = ++fetchVersion.current
    if (!user) {
      setLogs([])
      setDurationMostWorn([])
      setLoading(false)
      return
    }
    if (!silent) setLoading(true)
    try {
      let query = supabase
        .from('wear_logs')
        .select('*')
        .eq('user_id', user.id)
        .order('worn_on', { ascending: false })
        .order('created_at', { ascending: false })
        .limit(limit)
      if (fragranceId) query = query.eq('fragrance_id', fragranceId)
      const { data, error: err } = await query
      if (err) throw err
      const rows = data || []
      let hydratedRows = rows
      let fetchedWhoLabels = []
      let fetchedWhereLabels = []
      if (rows.length > 0) {
        const wearLogIds = rows.map(row => row.id)
        const [
          applicationsResult,
          complimentsResult,
          wearContextsResult,
          whoLabelsResult,
          whereLabelsResult,
        ] = await Promise.all([
          supabase.from('wear_applications').select('*').eq('user_id', user.id)
            .in('wear_log_id', wearLogIds).order('applied_at', { ascending: true }),
          supabase.from('compliment_events')
            .select('id,wear_log_id,relationship_type,occurred_at,created_at,context_tag,who_label_id,where_label_id')
            .eq('user_id', user.id).in('wear_log_id', wearLogIds).order('occurred_at', { ascending: true }),
          supabase.from('wear_log_context_tags').select('wear_log_id,context_tag,created_at')
            .eq('user_id', user.id).in('wear_log_id', wearLogIds).order('created_at', { ascending: true }),
          supabase.from('compliment_who_labels').select('id,label,last_used_at')
            .eq('user_id', user.id).order('last_used_at', { ascending: false }).limit(12),
          supabase.from('compliment_where_labels').select('id,label,last_used_at')
            .eq('user_id', user.id).order('last_used_at', { ascending: false }).limit(12),
        ])
        const firstError = [applicationsResult, complimentsResult, wearContextsResult, whoLabelsResult, whereLabelsResult]
          .find(result => result.error)?.error
        if (firstError) throw firstError
        const applications = applicationsResult.data || []
        const applicationsByLog = (applications || []).reduce((grouped, app) => {
          grouped[app.wear_log_id] = [...(grouped[app.wear_log_id] || []), app]
          return grouped
        }, {})
        const referencedLabels = await referencedComplimentLabels(supabase, user.id, complimentsResult.data || [], { who: whoLabelsResult.data, where: whereLabelsResult.data })
        hydratedRows = hydrateComplimentData(
          rows.map(row => ({ ...row, applications: applicationsByLog[row.id] || [] })),
          {
            events: complimentsResult.data,
            wearContexts: wearContextsResult.data,
            whoLabels: referencedLabels.who,
            whereLabels: referencedLabels.where,
          },
        )
        fetchedWhoLabels = whoLabelsResult.data || []
        fetchedWhereLabels = whereLabelsResult.data || []
      }
      const fetchedContextTags = await fetchCanonicalContextTags(supabase)
      let fetchedDurationMostWorn = []
      if (primaryWearModelEnabled && !fragranceId) {
        const { data: durationRows, error: durationError } = await supabase.rpc('get_most_worn_by_duration', {
          p_minimum_seconds: MOST_WORN_MINIMUM_SECONDS,
        })
        if (durationError) throw durationError
        fetchedDurationMostWorn = durationRows || []
      }
      if (version !== fetchVersion.current) return
      setDurationMostWorn(fetchedDurationMostWorn)
      setContextTags(fetchedContextTags)
      setRecentWhoLabels(fetchedWhoLabels)
      setRecentWhereLabels(fetchedWhereLabels)

      const reconciled = reconcileWearLogSnapshot(
        hydratedRows,
        pendingChanges.current,
        { limit, fragranceId },
      )
      pendingChanges.current = reconciled.pendingChanges
      logSources.current = new Map(reconciled.logs.map(row => [
        row.id,
        pendingChanges.current.has(row.id) ? 'in_memory_store' : 'query_result',
      ]))
      // A fetch that started before background weather landed must not wipe it.
      setLogs(reconciled.logs.map(log => {
        const patch = weatherPatches.current.get(log.id)
        if (!patch) return log
        if (Number.isFinite(log?.context_weather?.temp_c)) { weatherPatches.current.delete(log.id); return log }
        return { ...log, ...patch }
      }))
      setError(null)
    } catch (e) {
      if (version === fetchVersion.current) setError(e.message)
    } finally {
      if (version === fetchVersion.current) setLoading(false)
    }
  }, [user, limit, fragranceId, primaryWearModelEnabled])

  useEffect(() => { fetchLogs() }, [fetchLogs])

  // Weather is captured a second or two after a wear is saved. Patch it into
  // the in-memory row so Home and Rotation show it without a reload.
  useEffect(() => subscribeWearWeather(({ wearLogId, context }) => {
    if (!wearLogId || !context) return
    weatherPatches.current.set(wearLogId, context)
    setLogs(current => current.map(log => (log.id === wearLogId ? { ...log, ...context } : log)))
  }), [])

  useEffect(() => {
    pendingChanges.current.clear()
    logSources.current.clear()
  }, [user?.id])

  // Capture sheets and their parent pages own separate hook instances. A
  // successful mutation is therefore published once to every mounted reader,
  // then reconciled with the database for trigger-maintained fields. A version
  // guard prevents an older in-flight read from overwriting that confirmed row.
  useEffect(() => subscribeToWearLogChanges(change => {
    if (!user || (change.userId && change.userId !== user.id)) return

    const changedId = change.id || change.row?.id
    const matchesFilter = !fragranceId || change.row?.fragrance_id === fragranceId
    if (changedId && change.type !== 'invalidated' && (matchesFilter || change.type === 'deleted')) {
      pendingChanges.current.set(changedId, change)
      if (change.type === 'deleted') logSources.current.delete(changedId)
      else logSources.current.set(changedId, 'in_memory_store')
    }

    setLogs(current => applyWearLogChange(current, change, { limit, fragranceId }))
    void fetchLogs({ silent: true })
  }), [user, limit, fragranceId, fetchLogs])

  const finishPendingCreate = async (request,{fresh=false}={}) => {
    if(!user || request.ownerId!==user.id)throw Error('Sign in to the account that started this wear save.')
    if(createWearLocks.has(user.id))throw Error('This wear save is already being checked.')
    createWearLocks.add(user.id);window.dispatchEvent(new Event(pendingWearEvent))
    try {
      const result=await sendPendingWear(supabase,request,{primaryModelEnabled:primaryWearModelEnabled})
      clearPendingWear(localStorage,request)
      window.dispatchEvent(new Event(pendingWearEvent))
      const row={...result.wear,applications:result.applications,context_tags:result.context_tags}
      publishWearLogChange({type:'created',userId:user.id,row});emitWearLogged()
      return row
    } catch(error) {
      if(fresh && isDefiniteWearRefusal(error) && !error.primarySelectionPending){clearPendingWear(localStorage,request);window.dispatchEvent(new Event(pendingWearEvent))}
      if(isSotdConflict(error))throw Error("You already have an SOTD logged for this day. Pick a wear label (Gym, Work, Night out…) to log it as another wear.")
      throw error
    } finally {createWearLocks.delete(user.id);window.dispatchEvent(new Event(pendingWearEvent))}
  }
  const withCreateLock=async action=>{
    if(!user)throw Error('Sign in before recording a wear.')
    if(!navigator.locks?.request)throw Error('This browser cannot safely coordinate a wear save. Try an updated browser.')
    return navigator.locks.request(`fos-create-wear:${user.id}`,{ifAvailable:true},lock=>{if(!lock)throw Error('This wear save is already being checked in another tab.');return action()})
  }
  const retryPendingCreate=async()=>withCreateLock(()=>{
    const request=readPendingWear(localStorage,user.id)
    if(!request)throw Error('No pending wear save was found.')
    return finishPendingCreate(request)
  })
  const addLog=async(entry,application=null,{contextTags:initialContextTags=[]}={})=>withCreateLock(()=>{
    const prepared=prepareWearSession(normalizeEntry(entry),application)
    const initial={applied_at:prepared.application.applied_at||new Date().toISOString(),sprays:prepared.application.sprays}
    const request=preparePendingWear(localStorage,user.id,prepared.session,initial,initialContextTags,{name:entry.fragrance_name,house:entry.house})
    window.dispatchEvent(new Event(pendingWearEvent))
    return finishPendingCreate(request,{fresh:true})
  })

  addLog.assertReady=()=>{if(!user)throw Error('Sign in before recording a wear.');if(readPendingWear(localStorage,user.id))throw Error('An earlier wear save is unconfirmed. Use Retry original save before starting another.')}

  const deleteLog = async (id) => {
    const { error: err } = await supabase
      .from('wear_logs')
      .delete()
      .eq('id', id)
      .eq('user_id', user.id)
    if (err) throw err
    publishWearLogChange({ type: 'deleted', userId: user.id, id })
  }

  const updateLog = async (id, patch, options = {}) => {
    const cleanPatch = normalizePatch(patch)
    if (Object.keys(cleanPatch).length === 0) {
      return logs.find(l => l.id === id) || null
    }
    let query = supabase.from('wear_logs').update(cleanPatch).eq('id', id).eq('user_id', user.id)
    // Compare-and-set prevents a stale layer form from replacing another edit.
    if (Object.hasOwn(options, 'expectedLayers')) {
      query = options.expectedLayers == null
        ? query.is('layered_with', null)
        : query.eq('layered_with', postgresTextArray(options.expectedLayers))
    }
    const { data, error: err } = await query.select().single()
    if (err && Object.hasOwn(options, 'expectedLayers') && err.code === 'PGRST116') {
      await fetchLogs()
      throw new Error('This wear changed while you were adding layers. Review its latest layers and try again.')
    }
    if (err) throw err
    publishWearLogChange({ type: 'updated', userId: user.id, row: data })
    return data
  }

  const appendCheckin = async (id, value, at) => {
    if (!user?.id) throw new Error('Sign in to record a check-in.')
    const owner=user.id
    const key=`fos-performance-request-v1:${owner}:${id}`
    const pending=JSON.parse(sessionStorage.getItem(key) || 'null')
    if (pending && pending.value !== value) throw new Error(`Confirm the pending ${pending.value} check-in before recording a different value.`)
    const event=pending || {id:crypto.randomUUID(),value,at:at ? new Date(at).toISOString() : new Date().toISOString()}
    sessionStorage.setItem(key,JSON.stringify(event))
    const { data,error }=await supabase.rpc('append_performance_checkin',{p_wear_id:id,p_event_id:event.id,p_value:event.value,p_at:event.at})
    if(error) {
      if(['P0001','42501','22007','22008'].includes(error.code)) sessionStorage.removeItem(key)
      throw error
    }
    if(!data?.id) throw new Error('Check-in not confirmed. Retry the same value.')
    sessionStorage.removeItem(key)
    publishWearLogChange({type:'updated',userId:owner,row:data})
    return {...event,wear:data}
  }

  const addApplication = async (
    wearLogId,
    { sprays = 1, applied_at = new Date().toISOString(), is_reapplication = false } = {},
  ) => {
    const { data, error: err } = await supabase
      .from('wear_applications')
      .insert({
        user_id: user.id,
        wear_log_id: wearLogId,
        is_reapplication,
        applied_at,
        sprays: sprays == null || sprays === '' ? null : Number(sprays),
      })
      .select()
      .single()
    if (err) throw err
    setLogs(prev => prev.map(log => {
      if (log.id !== wearLogId) return log
      const applications = [...(log.applications || []), data]
        .sort((a, b) => new Date(a.applied_at) - new Date(b.applied_at))
      return { ...log, applications, sprays: applicationsTotal(applications) }
    }))
    publishWearLogChange({ type: 'invalidated', userId: user.id, wearLogId })
    return data
  }

  const addReapplication = (wearLogId, application = {}) => addApplication(wearLogId, {
    ...application,
    is_reapplication: true,
  })

  const addInitialApplication = (wearLogId, application = {}) => addApplication(wearLogId, {
    ...application,
    is_reapplication: false,
  })

  // One transaction creates the event and reuses/upserts optional private
  // labels. The RPC never accepts quote text and the UI never writes either
  // legacy compliment column.
  const addComplimentEvent = async (wearLogId, detail) => {
    const data = await createComplimentEvent(supabase, wearLogId, detail)
    publishWearLogChange({ type: 'invalidated', userId: user.id, wearLogId })
    return data
  }

  const removeComplimentEvent = async (wearLogId, eventId) => {
    const id = await deleteComplimentEvent(supabase, user.id, eventId)
    publishWearLogChange({ type: 'invalidated', userId: user.id, wearLogId })
    return id
  }

  const addWearContextTag = async (wearLogId, contextTag) => {
    const { data, error: err } = await supabase
      .from('wear_log_context_tags')
      .insert({ wear_log_id: wearLogId, user_id: user.id, context_tag: contextTag })
      .select()
      .single()
    if (err) throw err
    publishWearLogChange({ type: 'invalidated', userId: user.id, wearLogId })
    return data
  }

  const updateApplication = async (applicationId, wearLogId, patch) => {
    const clean = {}
    if ('sprays' in patch) clean.sprays = patch.sprays == null || patch.sprays === '' ? null : Number(patch.sprays)
    if ('applied_at' in patch) clean.applied_at = patch.applied_at
    const { data, error: err } = await supabase
      .from('wear_applications')
      .update(clean)
      .eq('id', applicationId)
      .eq('user_id', user.id)
      .select()
      .single()
    if (err) throw err
    setLogs(prev => prev.map(log => {
      if (log.id !== wearLogId) return log
      const applications = (log.applications || [])
        .map(app => app.id === applicationId ? data : app)
        .sort((a, b) => new Date(a.applied_at) - new Date(b.applied_at))
      return { ...log, applications, sprays: applicationsTotal(applications) }
    }))
    publishWearLogChange({ type: 'invalidated', userId: user.id, wearLogId })
    return data
  }

  const deleteApplication = async (applicationId, wearLogId) => {
    const { error: err } = await supabase
      .from('wear_applications')
      .delete()
      .eq('id', applicationId)
      .eq('user_id', user.id)
    if (err) throw err
    setLogs(prev => prev.map(log => {
      if (log.id !== wearLogId) return log
      const applications = (log.applications || []).filter(app => app.id !== applicationId)
      return { ...log, applications, sprays: applicationsTotal(applications) }
    }))
    publishWearLogChange({ type: 'invalidated', userId: user.id, wearLogId })
  }

  return {
    logs,
    durationMostWorn,
    loading,
    error,
    addLog,
    pendingCreate,
    pendingCreateError,
    createInFlight:user?createWearLocks.has(user.id):false,
    retryPendingCreate,
    addReapplication,
    addInitialApplication,
    addComplimentEvent,
    removeComplimentEvent,
    addWearContextTag,
    updateApplication,
    deleteApplication,
    updateLog,
    appendCheckin,
    deleteLog,
    refresh: () => fetchLogs(),
    ownerUserId: user?.id || null,
    contextTags,
    recentWhoLabels,
    recentWhereLabels,
    getLogSource: log => logSources.current.get(log?.id) || 'unknown',
  }
}
